import { COMPONENT_PER_SLEUTEL, MAX_ONDERDELEN } from "./dierComponenten";
import type { Dichtheid } from "./Gezondheidsring";
import type { VormSleutel } from "./tegelOntwerpen";

/**
 * "Mijn combinatie": een tegel die de gebruiker zelf samenstelt.
 *
 * <p>Uit het ontwerp van 30-09-2026: de configuratie bestaat uit kiezen, ordenen en vormgeven, en er
 * worden <b>geen diergegevens in de tegelconfiguratie opgeslagen</b>. Wat hier staat is dus een recept en
 * geen inhoud: welke onderdelen, in welke volgorde, in welke vorm. Ieder onderdeel haalt zijn eigen
 * gegevens op zodra de tegel getoond wordt.
 *
 * <p>Dat onderscheid is niet academisch. Zou de configuratie waarden bewaren, dan toont een tegel na een
 * week het gewicht van vorige week en heeft niemand door dat hij naar een kopie kijkt.
 */

export interface SamengesteldeTegel {
  /** Eigen id, zodat een gebruiker er meerdere kan hebben met dezelfde vorm. */
  id: string;
  /** Wat de gebruiker hem noemt; leeg mag, dan valt het scherm terug op de naam van de vorm. */
  naam: string;
  vorm: VormSleutel;
  /** Sleutels uit de componentbibliotheek, in de volgorde waarin ze getoond worden. */
  componenten: string[];
  /**
   * Automatisch schalen: het systeem kiest de dichtheid uit het aantal zichtbare tegels.
   *
   * <p>Staat standaard aan, zoals het ontwerp voorschrijft. Uit betekent dat de tegel zijn eigen
   * {@code vasteDichtheid} houdt, ook als er tien tegels naast elkaar staan.
   */
  automatischSchalen: boolean;
  /** Alleen gebruikt als automatisch schalen uit staat. */
  vasteDichtheid: Dichtheid;
  /**
   * Blijft bestaan voor wat er al bewaard is.
   *
   * <p>De bouwer had een schakelaar voor de hele tegel náást die per onderdeel. Twee schakelaars voor
   * hetzelfde laten je raden welke wint, dus de tegelbrede is weg. Wat er met {@code false} bewaard
   * staat wordt bij het inlezen vertaald naar "alle onderdelen uit", zodat die tegels blijven doen wat
   * iemand bedoelde.
   *
   * @deprecated gebruik {@link SamengesteldeTegel.actiesUit}
   */
  snelleActies: boolean;
  /**
   * De onderdelen waarvan de snelle actie uit staat.
   *
   * <p>Per onderdeel en niet alleen voor de hele tegel: op een verzorgingstegel wil je wél snel een
   * weging kunnen bijschrijven maar niet per ongeluk het voedingsschema overschrijven. Leeg betekent
   * dat ze allemaal aan staan, zodat een bestaande tegel niets verandert.
   */
  actiesUit: string[];
  /**
   * De foto van het dier in het midden van de ring, in plaats van het gewicht.
   *
   * <p>Alleen zinvol bij de ronde stijl: de andere stijlen hebben geen midden. Een keuze en geen vaste
   * regel, want wie een tegel maakt om gewichten te volgen wil daar het getal zien en niet de foto die
   * hij in de kop al heeft gezien.
   */
  fotoInMidden: boolean;
  /**
   * Toont elk onderdeel wanneer het voor het laatst geregistreerd is.
   *
   * <p>Aan is de standaard: "20,6 kg" zonder datum zegt niet of die meting van vandaag is of van een
   * jaar geleden. Uit kan nuttig zijn op een smalle tegel, waar de datum de waarde wegdrukt.
   */
  toonHerkomst: boolean;
  /** Bij drie of meer tegels krijgt de hoogste prioriteit de meeste ruimte. */
  prioriteit: number;
  /**
   * Hoe breed de tegel op het dierscherm staat: de hele rij, een halve of een kwart (één kolom).
   *
   * <p>Gekozen in het voorbeeld van de bouwer, zodat je krijgt wat je daar ziet. Daarvoor was die keuze
   * alleen een voorbeeld en kreeg elke eigen tegel één kolom: een ronde tegel werd dan zo smal dat de
   * waarden onder de cirkel zakten, terwijl hij in de bouwer er netjes naast stond.
   */
  breedte: TegelBreedte;
}

export type TegelBreedte = "heel" | "half" | "kwart";

export const TEGEL_BREEDTES: TegelBreedte[] = ["heel", "half", "kwart"];

/**
 * De breedte van een tegel die er nog geen heeft (bewaard voor die keuze bestond).
 *
 * <p>Rond en de halve meter zetten hun waarden náást de cirkel, en dat past niet in één kolom; die
 * krijgen een halve rij. De rest hield één kolom en houdt die.
 */
export function standaardBreedte(vorm: VormSleutel): TegelBreedte {
  return vorm === "ring" || vorm === "halvemeter" ? "half" : "kwart";
}

/**
 * De stijlen die de tegelbouwer aanbiedt.
 *
 * <p>Rond, raster en lijst voorop: dat zijn de drie die voor een willekeurige combinatie onderdelen
 * altijd werken, en voor de meeste tegels is het daarmee klaar. De overige vormen staan erachter, want
 * ze bestaan en er is geen reden om ze achter te houden -- alleen hebben ze een combinatie nodig die bij
 * ze past. Een prijskaart met vier medische onderdelen erin ziet er niet uit; dat is te zien in het
 * voorbeeld, en dat is ook waar het voorbeeld voor is.
 */
export const BOUWER_STIJLEN = [
  { stijl: "ring" as const, label: "dierTegel.stijl.rond" },
  { stijl: "satellieten" as const, label: "dierTegel.stijl.raster" },
  { stijl: "statuslijst" as const, label: "dierTegel.stijl.lijst" },
  { stijl: "halvemeter" as const, label: "dierTegel.vorm.halvemeter" },
  { stijl: "boogmeter" as const, label: "dierTegel.vorm.boogmeter" },
  { stijl: "waardenboog" as const, label: "dierTegel.vorm.waardenboog" },
  { stijl: "gesplitst" as const, label: "dierTegel.vorm.gesplitst" },
  { stijl: "radiaal" as const, label: "dierTegel.vorm.radiaal" },
  { stijl: "tweekolommen" as const, label: "dierTegel.vorm.tweekolommen" },
  { stijl: "kwadranten" as const, label: "dierTegel.vorm.kwadranten" },
  { stijl: "prijskaart" as const, label: "dierTegel.vorm.prijskaart" },
];

/** Hoeveel eigen tegels iemand er mag hebben. */
export const MAX_SAMENGESTELD = 6;

const VORMEN: VormSleutel[] = [
  "ring", "halvemeter", "satellieten", "gesplitst", "statuslijst",
  "radiaal", "prijskaart", "tweekolommen", "boogmeter", "waardenboog", "kwadranten",
];

const DICHTHEDEN: Dichtheid[] = ["compact", "normaal", "uitgebreid"];

/** Een verse tegel: rond, leeg, automatisch geschaald, met acties aan. */
export function nieuweSamengesteldeTegel(prioriteit = 0): SamengesteldeTegel {
  return {
    id: `eigen-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    naam: "",
    vorm: "ring",
    componenten: [],
    automatischSchalen: true,
    vasteDichtheid: "normaal",
    snelleActies: true,
    actiesUit: [],
    fotoInMidden: true,
    toonHerkomst: true,
    prioriteit,
    breedte: standaardBreedte("ring"),
  };
}

function isObject(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null;
}

/**
 * Leest wat er bewaard staat en gooit weg wat niet meer klopt.
 *
 * <p>Defensief, net als de rest van de voorkeur: een onderdeel dat uit de bibliotheek verdwijnt, een
 * vorm die niet meer bestaat, of een lijst die ooit te lang is geworden mag geen leeg scherm opleveren.
 * Wat overblijft is altijd toonbaar; wat weg is, is stil weg -- de gebruiker ziet dat vanzelf in de
 * bouwer en kan het opnieuw kiezen.
 */
export function leesSamengestelde(ruw: unknown): SamengesteldeTegel[] {
  if (!Array.isArray(ruw)) {
    return [];
  }
  const uit: SamengesteldeTegel[] = [];
  for (const item of ruw) {
    if (!isObject(item) || typeof item.id !== "string") {
      continue;
    }
    const vorm = VORMEN.includes(item.vorm as VormSleutel) ? (item.vorm as VormSleutel) : "ring";
    const componenten = Array.isArray(item.componenten)
      ? [...new Set(item.componenten.filter((s): s is string => typeof s === "string" && s in COMPONENT_PER_SLEUTEL))]
          .slice(0, MAX_ONDERDELEN)
      : [];
    uit.push({
      id: item.id,
      naam: typeof item.naam === "string" ? item.naam : "",
      vorm,
      componenten,
      automatischSchalen: item.automatischSchalen !== false,
      vasteDichtheid: DICHTHEDEN.includes(item.vasteDichtheid as Dichtheid)
        ? (item.vasteDichtheid as Dichtheid)
        : "normaal",
      snelleActies: item.snelleActies !== false,
      fotoInMidden: item.fotoInMidden !== false,
      toonHerkomst: item.toonHerkomst !== false,
      // Een tegel waarvan ooit de tegelbrede schakelaar uit stond: dat betekende "geen enkele actie",
      // en dat blijft zo nu die schakelaar weg is.
      actiesUit: item.snelleActies === false
        ? componenten
        : Array.isArray(item.actiesUit)
          ? item.actiesUit.filter((x): x is string => typeof x === "string" && x in COMPONENT_PER_SLEUTEL)
          : [],
      prioriteit: typeof item.prioriteit === "number" && Number.isFinite(item.prioriteit) ? item.prioriteit : 0,
      breedte: TEGEL_BREEDTES.includes(item.breedte as TegelBreedte)
        ? (item.breedte as TegelBreedte)
        : standaardBreedte(vorm),
    });
    if (uit.length >= MAX_SAMENGESTELD) {
      break;
    }
  }
  return uit;
}

/**
 * Of een onderdeel nog gekozen mag worden.
 *
 * <p>Twee grenzen. De zes uit het ontwerp, omdat een tegel met acht dingen erin geen tegel meer is. En
 * de dagprijs, die alleen bestaat als het pension per dier rekent -- hem aanbieden waar hij niet geldt
 * levert een tegel op met een leeg vak dat nooit vult.
 */
export function magKiezen(
  tegel: SamengesteldeTegel,
  sleutel: string,
  tariefPerDier: boolean,
): { mag: boolean; reden?: "vol" | "nietVanToepassing" } {
  if (tegel.componenten.includes(sleutel)) {
    return { mag: true };
  }
  const def = COMPONENT_PER_SLEUTEL[sleutel];
  if (def?.alleenBijTariefPerDier && !tariefPerDier) {
    return { mag: false, reden: "nietVanToepassing" };
  }
  if (tegel.componenten.length >= MAX_ONDERDELEN) {
    return { mag: false, reden: "vol" };
  }
  return { mag: true };
}

/** Een onderdeel erbij of eraf, met de grens erin verwerkt. */
export function wissel(tegel: SamengesteldeTegel, sleutel: string, tariefPerDier: boolean): SamengesteldeTegel {
  if (tegel.componenten.includes(sleutel)) {
    return { ...tegel, componenten: tegel.componenten.filter((s) => s !== sleutel) };
  }
  if (!magKiezen(tegel, sleutel, tariefPerDier).mag) {
    return tegel;
  }
  return { ...tegel, componenten: [...tegel.componenten, sleutel] };
}

/** De snelle actie van één onderdeel aan of uit. */
export function wisselActie(tegel: SamengesteldeTegel, sleutel: string): SamengesteldeTegel {
  const uit = tegel.actiesUit.includes(sleutel);
  return {
    ...tegel,
    actiesUit: uit ? tegel.actiesUit.filter((s) => s !== sleutel) : [...tegel.actiesUit, sleutel],
  };
}

/**
 * Of de snelle actie van dit onderdeel getoond wordt.
 *
 * <p>Alleen de keuze per onderdeel; de oude tegelbrede schakelaar is bij het inlezen al vertaald. Dit is
 * een weergavekeuze en geen recht: zonder schrijfrecht verdwijnt de knop sowieso, en deze schakelaar kan
 * er nooit een tevoorschijn toveren.
 */
export function toontActie(tegel: SamengesteldeTegel, sleutel: string): boolean {
  return !tegel.actiesUit.includes(sleutel);
}

/**
 * Een onderdeel naar een andere plek in de lijst.
 *
 * <p>Voor slepen: {@code naar} is de plek waar het terechtkomt, niet het aantal stappen. Buiten de lijst
 * gebeurt er niets.
 */
export function verplaatsNaar(tegel: SamengesteldeTegel, sleutel: string, naar: number): SamengesteldeTegel {
  const i = tegel.componenten.indexOf(sleutel);
  if (i < 0 || naar < 0 || naar >= tegel.componenten.length || naar === i) {
    return tegel;
  }
  const lijst = [...tegel.componenten];
  lijst.splice(i, 1);
  lijst.splice(naar, 0, sleutel);
  return { ...tegel, componenten: lijst };
}

/** Een onderdeel een plek omhoog of omlaag. Buiten de lijst gebeurt er niets. */
export function verplaats(tegel: SamengesteldeTegel, sleutel: string, richting: -1 | 1): SamengesteldeTegel {
  const i = tegel.componenten.indexOf(sleutel);
  const j = i + richting;
  if (i < 0 || j < 0 || j >= tegel.componenten.length) {
    return tegel;
  }
  const lijst = [...tegel.componenten];
  [lijst[i], lijst[j]] = [lijst[j], lijst[i]];
  return { ...tegel, componenten: lijst };
}
